import React, { useState, useEffect } from 'react';
import {
  Smartphone,
  Wifi,
  WifiOff,
  CloudLightning,
  Bell,
  Download,
  Share2,
  Database,
  Layers,
  Activity,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Trash2,
  Shield,
  Gauge,
  Sliders,
  Send,
  Zap,
  Globe,
  Lock,
  Compass,
  FileCode,
  ArrowRight,
  HardDrive
} from 'lucide-react';
import { AppView } from '../../types';
import { OFFLINE_TOOL_REGISTRY, OfflineStorageManager } from '../../services/mobile/offlineEngine';
import { detectDeviceCapabilities } from '../../services/mobile/capabilityRegistry';
import { networkEngine, DataSaverPreference } from '../../services/mobile/networkStateEngine';
import { syncEngine } from '../../services/mobile/syncEngine';
import { PushService } from '../../services/mobile/pushService';
import { PWAInstallService } from '../../services/mobile/pwaInstallService';
import {
  MOBILE_EXECUTIVE_METRICS,
  MOBILE_PERFORMANCE_BENCHMARKS,
  OS_PLATFORM_MATRIX,
  MOBILE_INCIDENTS
} from '../../data/mobileMockData';
import { SyncOperation, DeviceCapabilityItem } from '../../services/mobile/offlineDataTypes';

type MobileTab =
  | 'overview'
  | 'pwa'
  | 'service-worker'
  | 'offline-tools'
  | 'sync-outbox'
  | 'web-push'
  | 'capabilities'
  | 'performance';

interface Props {
  onNavigateToView: (v: AppView) => void;
}

export const MobileExperienceCommandCenter: React.FC<Props> = ({ onNavigateToView }) => {
  const [activeTab, setActiveTab] = useState<MobileTab>('overview');
  const [capabilities, setCapabilities] = useState<DeviceCapabilityItem[]>([]);
  const [networkState, setNetworkState] = useState(networkEngine.getNetworkState());
  const [isDataSaver, setIsDataSaver] = useState(networkEngine.isDataSaverActive());
  const [dataSaverPref, setDataSaverPref] = useState<DataSaverPreference>(networkEngine.getDataSaverPreference());
  const [outbox, setOutbox] = useState<SyncOperation[]>([]);
  const [storageInfo, setStorageInfo] = useState({ usageMb: 4.2, quotaMb: 500, pctUsed: 1 });
  const [cacheClearNotice, setCacheClearNotice] = useState<string | null>(null);
  const [canInstall, setCanInstall] = useState(PWAInstallService.isInstallEligible());
  const [testPushStatus, setTestPushStatus] = useState<string | null>(null);

  useEffect(() => {
    setCapabilities(detectDeviceCapabilities());
    OfflineStorageManager.getStorageEstimate().then(setStorageInfo);

    const unsubNet = networkEngine.subscribe((state, ds) => {
      setNetworkState(state);
      setIsDataSaver(ds);
    });

    const unsubSync = syncEngine.subscribe(setOutbox);
    const unsubInstall = PWAInstallService.subscribe(setCanInstall);

    return () => {
      unsubNet();
      unsubSync();
      unsubInstall();
    };
  }, []);

  const handleClearCache = () => {
    const res = OfflineStorageManager.clearAllOfflineFinancialData();
    setCacheClearNotice(`Cleared ${res.clearedCount} local offline envelopes at ${new Date(res.timestamp).toLocaleTimeString()}`);
    OfflineStorageManager.getStorageEstimate().then(setStorageInfo);
    setTimeout(() => setCacheClearNotice(null), 4000);
  };

  const handleDataSaverToggle = (pref: DataSaverPreference) => {
    setDataSaverPref(pref);
    networkEngine.setDataSaverPreference(pref);
  };

  const handleSimulateSync = () => {
    syncEngine.enqueueOperation('SAVE_CALCULATION', {
      calcType: 'MORTGAGE',
      principal: 350000,
      rate: 3.85,
      timestamp: new Date().toISOString()
    });
  };

  const handleTestPush = async () => {
    setTestPushStatus('Requesting contextual permission...');
    const item = await PushService.subscribeToCategory('FX_ALERT');
    if (item) {
      setTestPushStatus('Subscribed successfully! Notification token registered.');
    } else {
      setTestPushStatus('Permission dismissed or unsupported on this device/browser.');
    }
    setTimeout(() => setTestPushStatus(null), 5000);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24 text-[#0F172A]">
      {/* Header Banner */}
      <div className="bg-[#0F172A] text-white pt-16 pb-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#1E40AF] rounded-full blur-[130px] opacity-25 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center gap-3 text-[#94A3B8] mb-4 text-sm font-medium">
            <Smartphone className="w-4 h-4 text-[#38BDF8]" />
            <span>YamooPay Platform Engineering</span>
            <span>/</span>
            <span className="text-white font-bold">Mobile & PWA Command Center (Prompt 19)</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-white mb-3">
                Global Mobile & Offline Runtime
              </h1>
              <p className="text-[#CBD5E1] text-base md:text-lg max-w-3xl leading-relaxed">
                Progressive Web App, Service Worker v1.19.0, Deterministic Offline Calculation Runtime, Smart Caching, Sync Outbox, and Web Push.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 border ${
                networkState === 'ONLINE' 
                  ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-300'
                  : 'bg-amber-950/60 border-amber-500/40 text-amber-300'
              }`}>
                {networkState === 'ONLINE' ? <Wifi className="w-4 h-4 text-emerald-400" /> : <WifiOff className="w-4 h-4 text-amber-400" />}
                <span>Network: {networkState}</span>
              </div>

              {isDataSaver && (
                <div className="px-4 py-2 bg-blue-950/60 border border-blue-500/40 text-blue-300 rounded-xl text-xs font-bold flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-400" />
                  <span>Data Saver: ON</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-20">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 mb-8">
          {/* Sub Navigation Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E2E8F0] pb-4">
            {[
              { id: 'overview', label: 'Executive Overview', icon: Activity },
              { id: 'pwa', label: 'PWA & Installability', icon: Download },
              { id: 'service-worker', label: 'Service Worker & Cache', icon: Layers },
              { id: 'offline-tools', label: 'Offline Tools Matrix (20+)', icon: Database },
              { id: 'sync-outbox', label: `Sync Outbox (${outbox.length})`, icon: RefreshCw },
              { id: 'web-push', label: 'Web Push & Privacy', icon: Bell },
              { id: 'capabilities', label: 'Device Capabilities', icon: Smartphone },
              { id: 'performance', label: 'CWV & Low-Bandwidth', icon: Gauge },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as MobileTab)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#1E40AF] text-white shadow-xs'
                      : 'bg-white text-[#475569] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Active Tab Views */}
          {activeTab === 'overview' && <OverviewTab onNavigateToView={onNavigateToView} />}
          {activeTab === 'pwa' && <PwaTab canInstall={canInstall} />}
          {activeTab === 'service-worker' && (
            <ServiceWorkerTab
              storageInfo={storageInfo}
              onClearCache={handleClearCache}
              cacheClearNotice={cacheClearNotice}
            />
          )}
          {activeTab === 'offline-tools' && <OfflineToolsTab onNavigateToView={onNavigateToView} />}
          {activeTab === 'sync-outbox' && (
            <SyncOutboxTab
              outbox={outbox}
              onSimulate={handleSimulateSync}
              onClear={() => syncEngine.clearOutbox()}
            />
          )}
          {activeTab === 'web-push' && (
            <WebPushTab
              onTestPush={handleTestPush}
              testPushStatus={testPushStatus}
            />
          )}
          {activeTab === 'capabilities' && <CapabilitiesTab capabilities={capabilities} />}
          {activeTab === 'performance' && (
            <PerformanceTab
              dataSaverPref={dataSaverPref}
              onDataSaverChange={handleDataSaverToggle}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* --- TAB 1: EXECUTIVE OVERVIEW --- */
function OverviewTab({ onNavigateToView }: { onNavigateToView: (v: AppView) => void }) {
  return (
    <div className="space-y-8">
      {/* Policy Banner: Light Mode Only & Mobile Excellence */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-start gap-4">
        <Shield className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
        <div>
          <h3 className="text-blue-950 font-bold text-base mb-1">
            CANONICAL MOBILE PRINCIPLE: OFFLINE DETERMINISM & RESPECTFUL PWA
          </h3>
          <p className="text-blue-900 text-sm leading-relaxed">
            YamooPay enforces deterministic client-side financial calculations that run identically online and offline. We forbid intrusive first-load install banners, fake native claims, and unannounced Push prompts. Financial data stored locally is always stamped with verified freshness metadata.
          </p>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
          <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Mobile Traffic Share</div>
          <div className="text-3xl font-black text-[#0F172A]">{MOBILE_EXECUTIVE_METRICS.mobileSharePct}%</div>
          <div className="text-xs text-[#059669] font-bold mt-1">482,900 monthly active users</div>
        </div>

        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
          <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Daily Offline Calculations</div>
          <div className="text-3xl font-black text-[#1E40AF]">{MOBILE_EXECUTIVE_METRICS.dailyOfflineCalculations}</div>
          <div className="text-xs text-[#059669] font-bold mt-1">100.0% deterministic match</div>
        </div>

        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
          <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Mobile P95 Latency</div>
          <div className="text-3xl font-black text-[#0F172A]">{MOBILE_EXECUTIVE_METRICS.p95MobileLatencyMs} ms</div>
          <div className="text-xs text-[#059669] font-bold mt-1">Core Web Vitals Pass: 98.2%</div>
        </div>

        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
          <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">PWA Standalone Return</div>
          <div className="text-3xl font-black text-[#0F172A]">{MOBILE_EXECUTIVE_METRICS.standaloneReturnRatePct}%</div>
          <div className="text-xs text-[#64748B] font-bold mt-1">14.8% install adoption</div>
        </div>
      </div>

      {/* Quick Launch & Quick Links */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
        <h3 className="text-lg font-bold text-[#0F172A] mb-4">Core Mobile Shortcuts & Entry Routes</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            onClick={() => onNavigateToView('everyday-command-center')}
            className="p-4 border border-[#E2E8F0] hover:border-[#1E40AF] rounded-xl text-left transition-all hover:shadow-xs group cursor-pointer"
          >
            <div className="font-bold text-[#0F172A] group-hover:text-[#1E40AF] flex items-center justify-between">
              <span>Universal Everyday Calcs</span>
              <ArrowRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#1E40AF]" />
            </div>
            <p className="text-xs text-[#64748B] mt-1">100% offline percentage, tip, margin, units</p>
          </button>

          <button
            onClick={() => onNavigateToView('currency-intelligence')}
            className="p-4 border border-[#E2E8F0] hover:border-[#1E40AF] rounded-xl text-left transition-all hover:shadow-xs group cursor-pointer"
          >
            <div className="font-bold text-[#0F172A] group-hover:text-[#1E40AF] flex items-center justify-between">
              <span>Currency Intelligence</span>
              <ArrowRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#1E40AF]" />
            </div>
            <p className="text-xs text-[#64748B] mt-1">Live mid-market & offline Last-Known-Good rates</p>
          </button>

          <button
            onClick={() => onNavigateToView('salary-engine')}
            className="p-4 border border-[#E2E8F0] hover:border-[#1E40AF] rounded-xl text-left transition-all hover:shadow-xs group cursor-pointer"
          >
            <div className="font-bold text-[#0F172A] group-hover:text-[#1E40AF] flex items-center justify-between">
              <span>Salary & Paycheck Tax</span>
              <ArrowRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#1E40AF]" />
            </div>
            <p className="text-xs text-[#64748B] mt-1">Gross-to-net calculations across 35+ jurisdictions</p>
          </button>
        </div>
      </div>
    </div>
  );
}

/* --- TAB 2: PWA & INSTALLABILITY --- */
function PwaTab({ canInstall }: { canInstall: boolean }) {
  const [installResult, setInstallResult] = useState<string | null>(null);

  const handleTriggerInstall = async () => {
    const res = await PWAInstallService.promptInstall();
    if (res === 'ACCEPTED') {
      setInstallResult('YamooPay added to device home screen successfully!');
    } else if (res === 'DISMISSED') {
      setInstallResult('Install prompt dismissed by user.');
    } else {
      setInstallResult('Install prompt unavailable (already installed or platform uses manual Add to Home Screen).');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-lg font-bold text-[#0F172A]">PWA Installability & Standalone Architecture</h3>
          <p className="text-sm text-[#64748B]">
            Standalone display mode, Web App Manifest v2026, Maskable icons, and Respectful promotion.
          </p>
        </div>

        <button
          onClick={handleTriggerInstall}
          className="px-5 py-2.5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-xl text-sm font-bold flex items-center gap-2 transition-all shadow-xs cursor-pointer"
        >
          <Download className="w-4 h-4" />
          <span>Install YamooPay (PWA)</span>
        </button>
      </div>

      {installResult && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-sm font-medium">
          {installResult}
        </div>
      )}

      {/* Manifest Fields Table */}
      <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
        <div className="bg-[#F8FAFC] px-4 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
          <span className="font-bold text-xs text-[#475569] uppercase tracking-wider">Web App Manifest Inspector</span>
          <span className="text-xs font-mono text-[#059669] font-bold">/manifest.webmanifest (Valid)</span>
        </div>
        <div className="divide-y divide-[#E2E8F0] text-sm">
          <div className="px-4 py-3 flex justify-between"><span className="text-[#64748B]">Application ID</span><span className="font-mono font-bold">yamoopay-financial-pwa</span></div>
          <div className="px-4 py-3 flex justify-between"><span className="text-[#64748B]">Display Mode</span><span className="font-bold text-[#059669]">standalone</span></div>
          <div className="px-4 py-3 flex justify-between"><span className="text-[#64748B]">Theme Color</span><span className="font-mono font-bold">#1E40AF (Light Mode)</span></div>
          <div className="px-4 py-3 flex justify-between"><span className="text-[#64748B]">Background Color</span><span className="font-mono font-bold">#FFFFFF</span></div>
          <div className="px-4 py-3 flex justify-between"><span className="text-[#64748B]">App Shortcuts</span><span className="font-bold">5 shortcuts (FX, Salary, Loans, Wealth, Everyday)</span></div>
          <div className="px-4 py-3 flex justify-between"><span className="text-[#64748B]">Share Target</span><span className="font-bold text-emerald-700">Supported (text / url / title)</span></div>
        </div>
      </div>

      {/* iOS Guidelines Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h4 className="font-bold text-amber-950 text-sm mb-1">iOS Safari WebKit Install Experience</h4>
        <p className="text-amber-900 text-xs leading-relaxed">
          On iOS/iPadOS, Apple does not fire <code className="font-mono bg-amber-100 px-1 py-0.5 rounded">beforeinstallprompt</code>. Users are guided to tap the Safari <strong>Share</strong> button and choose <strong>"Add to Home Screen"</strong>. YamooPay provides standard Apple Touch Icons and standalone viewport insets.
        </p>
      </div>
    </div>
  );
}

/* --- TAB 3: SERVICE WORKER & CACHING --- */
function ServiceWorkerTab({
  storageInfo,
  onClearCache,
  cacheClearNotice
}: {
  storageInfo: { usageMb: number; quotaMb: number; pctUsed: number };
  onClearCache: () => void;
  cacheClearNotice: string | null;
}) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-lg font-bold text-[#0F172A]">Service Worker Lifecycle & Cache Governance</h3>
          <p className="text-sm text-[#64748B]">
            Active Worker Version: <strong>v1.19.0</strong> | Strict Resource Categorization
          </p>
        </div>

        <button
          onClick={onClearCache}
          className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-xl text-sm font-bold flex items-center gap-2 transition-colors cursor-pointer"
        >
          <Trash2 className="w-4 h-4" />
          <span>Clear Cached Financial Data</span>
        </button>
      </div>

      {cacheClearNotice && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-sm font-bold">
          {cacheClearNotice}
        </div>
      )}

      {/* Cache Classes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-[#E2E8F0] rounded-xl p-4 bg-[#F8FAFC]">
          <div className="font-bold text-[#0F172A] mb-1">STATIC_IMMUTABLE (Cache First)</div>
          <p className="text-xs text-[#64748B] leading-relaxed">
            Hashed JS/CSS chunks, SVGs, and web fonts. Stored permanently in <code className="font-mono">yamoopay-static-v1.19.0</code> until new deployment.
          </p>
        </div>

        <div className="border border-[#E2E8F0] rounded-xl p-4 bg-[#F8FAFC]">
          <div className="font-bold text-[#0F172A] mb-1">PUBLIC_FINANCIAL_REFERENCE (Stale-While-Revalidate)</div>
          <p className="text-xs text-[#64748B] leading-relaxed">
            FX rate snapshots, tax brackets, and official constants. Stored with verified timestamp & offline metadata envelope.
          </p>
        </div>

        <div className="border border-[#E2E8F0] rounded-xl p-4 bg-[#F8FAFC]">
          <div className="font-bold text-[#0F172A] mb-1">NETWORK_ONLY / NO_STORE</div>
          <p className="text-xs text-[#64748B] leading-relaxed">
            Live transfer quotes, AI queries, auth tokens, admin routes, and CISO controls. Never written to public cache.
          </p>
        </div>

        <div className="border border-[#E2E8F0] rounded-xl p-4 bg-[#F8FAFC]">
          <div className="font-bold text-[#0F172A] mb-1">OFFLINE_SHELL_FALLBACK</div>
          <p className="text-xs text-[#64748B] leading-relaxed">
            Pre-rendered HTML shell loaded automatically upon offline navigation to prevent blank browser error pages.
          </p>
        </div>
      </div>

      {/* Storage Quota Card */}
      <div className="border border-[#E2E8F0] rounded-xl p-5 bg-white">
        <div className="flex justify-between items-center mb-2">
          <span className="font-bold text-sm text-[#0F172A]">Client Storage Quota</span>
          <span className="text-sm font-bold text-[#64748B]">{storageInfo.usageMb} MB / {storageInfo.quotaMb} MB</span>
        </div>
        <div className="w-full bg-[#F1F5F9] rounded-full h-3 overflow-hidden">
          <div className="bg-[#1E40AF] h-full rounded-full transition-all" style={{ width: `${Math.max(2, storageInfo.pctUsed)}%` }} />
        </div>
      </div>
    </div>
  );
}

/* --- TAB 4: OFFLINE TOOLS MATRIX --- */
function OfflineToolsTab({ onNavigateToView }: { onNavigateToView: (v: AppView) => void }) {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-[#0F172A]">Universal Offline Calculation Coverage</h3>
          <p className="text-sm text-[#64748B]">
            All 20+ deterministic engines verified to execute identically online and offline.
          </p>
        </div>
        <span className="px-3 py-1 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold rounded-full">
          20+ Offline Tools Active
        </span>
      </div>

      <div className="border border-[#E2E8F0] rounded-xl overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <tr>
              <th className="px-4 py-3 font-bold text-[#475569]">Tool Name</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Category</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Offline Capability</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Math Guarantee</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {OFFLINE_TOOL_REGISTRY.map((t) => (
              <tr key={t.toolId} className="hover:bg-[#F8FAFC]">
                <td className="px-4 py-3 font-bold text-[#0F172A]">{t.title}</td>
                <td className="px-4 py-3 text-xs text-[#64748B] font-mono">{t.category}</td>
                <td className="px-4 py-3">
                  {t.capability === 'FULL_OFFLINE' ? (
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded border border-emerald-200">
                      FULL OFFLINE
                    </span>
                  ) : t.capability === 'OFFLINE_WITH_CACHED_DATA' ? (
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-bold rounded border border-blue-200">
                      CACHED DATA
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-bold rounded border border-amber-200">
                      ONLINE REQUIRED
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-[#059669] font-bold">
                  {t.offlineDeterministic ? 'Deterministic (Identical)' : 'Server Model'}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onNavigateToView(t.toolId as AppView)}
                    className="text-[#1E40AF] hover:underline font-bold text-xs cursor-pointer"
                  >
                    Open Tool &rarr;
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --- TAB 5: SYNC OUTBOX --- */
function SyncOutboxTab({
  outbox,
  onSimulate,
  onClear
}: {
  outbox: SyncOperation[];
  onSimulate: () => void;
  onClear: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-bold text-[#0F172A]">Client Sync Outbox</h3>
          <p className="text-sm text-[#64748B]">
            Queued offline mutations executed with idempotency keys upon reconnection.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onSimulate}
            className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
          >
            + Enqueue Offline Save
          </button>
          <button
            onClick={onClear}
            className="px-4 py-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#475569] rounded-xl text-xs font-bold transition-colors cursor-pointer"
          >
            Clear Outbox
          </button>
        </div>
      </div>

      {outbox.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-[#CBD5E1] rounded-2xl bg-[#F8FAFC]">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
          <div className="font-bold text-[#0F172A]">Outbox is clear</div>
          <p className="text-xs text-[#64748B] mt-1">All mutations synced cleanly with server.</p>
        </div>
      ) : (
        <div className="border border-[#E2E8F0] rounded-xl overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr>
                <th className="px-4 py-3 font-bold text-[#475569]">Operation</th>
                <th className="px-4 py-3 font-bold text-[#475569]">Idempotency Key</th>
                <th className="px-4 py-3 font-bold text-[#475569]">Created</th>
                <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {outbox.map((op) => (
                <tr key={op.id}>
                  <td className="px-4 py-3 font-bold text-[#0F172A]">{op.operationType}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{op.idempotencyKey}</td>
                  <td className="px-4 py-3 text-xs text-[#64748B]">{new Date(op.createdAt).toLocaleTimeString()}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded">
                      {op.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* --- TAB 6: WEB PUSH & NOTIFICATIONS --- */
function WebPushTab({
  onTestPush,
  testPushStatus
}: {
  onTestPush: () => void;
  testPushStatus: string | null;
}) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-bold text-[#0F172A]">Contextual Web Push & Notification Privacy</h3>
          <p className="text-sm text-[#64748B]">
            Zero unprompted permission spam. Lock-screen privacy filtering protects sensitive financial figures.
          </p>
        </div>

        <button
          onClick={onTestPush}
          className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer"
        >
          <Bell className="w-4 h-4" />
          <span>Subscribe to FX Target Alerts</span>
        </button>
      </div>

      {testPushStatus && (
        <div className="p-4 bg-blue-50 border border-blue-200 text-blue-950 rounded-xl text-sm font-medium">
          {testPushStatus}
        </div>
      )}

      {/* Lock Screen Privacy Rules Box */}
      <div className="border border-[#E2E8F0] rounded-xl p-5 bg-white">
        <h4 className="font-bold text-[#0F172A] text-sm mb-3">Lock-Screen Notification Sanitization Rules</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <span className="font-bold text-red-900">FORBIDDEN ON LOCK SCREEN:</span>
            <p className="text-red-800 mt-1">
              "Your $450,000 mortgage interest changed" or "Paycheck net salary $6,450 ready".
            </p>
          </div>
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <span className="font-bold text-emerald-900">PERMITTED PRIVACY-SAFE FORMAT:</span>
            <p className="text-emerald-800 mt-1">
              "USD/EUR reached your target" or "New 2026 tax rules published for your region".
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --- TAB 7: DEVICE CAPABILITIES --- */
function CapabilitiesTab({ capabilities }: { capabilities: DeviceCapabilityItem[] }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-[#0F172A]">Live Device Capability Registry</h3>
        <p className="text-sm text-[#64748B]">
          Direct browser feature detection without unsafe user-agent string assumptions.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {capabilities.map((cap) => (
          <div key={cap.id} className="border border-[#E2E8F0] rounded-xl p-4 bg-white">
            <div className="flex justify-between items-start mb-2">
              <span className="font-bold text-sm text-[#0F172A]">{cap.name}</span>
              <span
                className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                  cap.status === 'SUPPORTED'
                    ? 'bg-emerald-100 text-emerald-800'
                    : cap.status === 'PARTIAL'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {cap.status}
              </span>
            </div>
            <p className="text-xs text-[#64748B] mb-2">{cap.description}</p>
            <div className="text-[11px] text-[#059669] font-medium">Purpose: {cap.purpose}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* --- TAB 8: PERFORMANCE & CORE WEB VITALS --- */
function PerformanceTab({
  dataSaverPref,
  onDataSaverChange
}: {
  dataSaverPref: DataSaverPreference;
  onDataSaverChange: (pref: DataSaverPreference) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-bold text-[#0F172A]">Mobile Performance & Low-Bandwidth Mode</h3>
          <p className="text-sm text-[#64748B]">
            Strict budgets for low-end Android devices, 3G emulation, and cellular optimization.
          </p>
        </div>

        <div className="flex items-center gap-2 bg-[#F1F5F9] p-1 rounded-xl border border-[#E2E8F0]">
          {(['AUTO', 'ON', 'OFF'] as DataSaverPreference[]).map((pref) => (
            <button
              key={pref}
              onClick={() => onDataSaverChange(pref)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                dataSaverPref === pref ? 'bg-[#1E40AF] text-white shadow-xs' : 'text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              {pref === 'AUTO' ? 'Auto (Network Info)' : pref === 'ON' ? 'Data Saver ON' : 'Data Saver OFF'}
            </button>
          ))}
        </div>
      </div>

      {/* CWV Benchmarks Table */}
      <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <tr>
              <th className="px-4 py-3 font-bold text-[#475569]">Device Tier</th>
              <th className="px-4 py-3 font-bold text-[#475569]">LCP (Target &lt; 2.5s)</th>
              <th className="px-4 py-3 font-bold text-[#475569]">INP (Target &lt; 200ms)</th>
              <th className="px-4 py-3 font-bold text-[#475569]">CLS (Target &lt; 0.1)</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {MOBILE_PERFORMANCE_BENCHMARKS.map((b) => (
              <tr key={b.deviceTier} className="hover:bg-[#F8FAFC]">
                <td className="px-4 py-3 font-bold text-[#0F172A]">{b.deviceTier}</td>
                <td className="px-4 py-3 font-mono text-xs">{b.lcpMs} ms</td>
                <td className="px-4 py-3 font-mono text-xs">{b.inpMs} ms</td>
                <td className="px-4 py-3 font-mono text-xs">{b.clsScore}</td>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-1 text-emerald-600 font-bold text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5" /> CWV PASS
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
